/**
 * @file     seo.ts
 * @purpose  Shared SEO defaults, site name, base URL, default OG image, Twitter handle.
 */

import { env } from '@/core/config/env';
import { brandConfig, resolveArticleAttribution, type ArticleAttribution } from '@/core/config/brand';

export const seoDefaults = {
  siteName: 'Relta Real Estate',
  siteUrl: env.siteUrl,
  defaultOgImage: '/og-default.jpg',
  twitterHandle: '@rayrasouli',
} as const;

const AREA_SERVED = [
  'Vancouver',
  'North Vancouver',
  'West Vancouver',
  'Burnaby',
  'Richmond',
  'Surrey',
  'Coquitlam',
  'Langley',
  'Maple Ridge',
  'Delta',
  'White Rock',
  'New Westminster',
  'Port Moody',
  'Port Coquitlam',
  'Squamish',
];

/**
 * The platform serves all of British Columbia; the city list above is the dense
 * Metro Vancouver core. Leading with the province stops the entity from being
 * read as a single-city agent site.
 */
const AREA_SERVED_GRAPH = [
  {
    '@type': 'State',
    name: 'British Columbia',
    containedInPlace: { '@type': 'Country', name: 'Canada' },
  },
  ...AREA_SERVED.map((name) => ({ '@type': 'City', name })),
];

/**
 * Names the entity is searched and referred to by. "Relta" alone is one edit
 * away from "Delta", a BC municipality in this same vertical, so the bare token
 * has to be claimed explicitly or it resolves to the municipality.
 */
const ALTERNATE_NAMES = ['Relta', 'relta.ca', 'Relta Real Estate BC'];

/** Topics the entity is an authority on. Feeds knowsAbout on the org + person. */
const KNOWS_ABOUT = [
  'British Columbia real estate',
  'MLS listings',
  'Metro Vancouver housing market',
  'Real estate market statistics',
  'Home buying in British Columbia',
  'Home selling in British Columbia',
  'Condo and strata property',
  'Property transfer tax',
  'Mortgage qualification',
];

/**
 * One sentence defining the entity, in the schema graph rather than only in
 * prose. AI systems build entity beliefs from parsed structured data; a site
 * that never states its own category in machine-readable form gets assigned a
 * guessed one.
 */
const ENTITY_DESCRIPTION =
  'Relta Real Estate is a British Columbia MLS® property search platform with live listings, ' +
  'map search, sold and expired listing data, and deterministic market statistics computed from the ' +
  'MLS® feed. It is operated by Ray Rasouli, a licensed REALTOR® with Real Broker B.C. Ltd. in ' +
  'Vancouver, British Columbia. It is not affiliated with the municipality of Delta, BC.';

const SAME_AS = [
  ...Object.values(brandConfig.social),
  ...brandConfig.authorityProfiles,
];

/**
 * The Relta Real Estate name Ray operates the site under. It is not a legal
 * entity and not a brokerage: the licensed agent (#agent) provides the real
 * estate services, so the brand node carries no legalName.
 */
export function getBrandOrganizationSchema() {
  const site = brandConfig.websiteBaseUrl;
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': `${site}/#company`,
    name: brandConfig.companyDisplayName,
    alternateName: [...brandConfig.brandAlternateNames],
    url: site,
    email: brandConfig.primaryContactEmail,
    logo: `${site}${seoDefaults.defaultOgImage}`,
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Vancouver',
      addressRegion: 'BC',
      addressCountry: 'CA',
    },
    founder: { '@id': `${site}/#ray` },
    knowsAbout: KNOWS_ABOUT,
    sameAs: SAME_AS,
  };
}

/**
 * JSON-LD structured data schemas for SEO.
 * RealEstateAgent extends LocalBusiness in schema.org, one type covers both.
 * This is the licensed service provider (Ray, with Real Broker B.C. Ltd.),
 * not the brand node.
 */
export function getRealEstateAgentSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'RealEstateAgent',
    '@id': `${brandConfig.websiteBaseUrl}/#agent`,
    name: brandConfig.agentName,
    alternateName: ALTERNATE_NAMES,
    description: ENTITY_DESCRIPTION,
    knowsAbout: KNOWS_ABOUT,
    url: brandConfig.websiteBaseUrl,
    telephone: brandConfig.phone,
    email: brandConfig.primaryContactEmail,
    address: {
      '@type': 'PostalAddress',
      streetAddress: '500-666 Burrard Street',
      addressLocality: 'Vancouver',
      addressRegion: 'BC',
      postalCode: 'V6C 3P6',
      addressCountry: 'CA',
    },
    parentOrganization: {
      '@type': 'RealEstateOrganization',
      name: brandConfig.brokerageName,
    },
    areaServed: AREA_SERVED_GRAPH,
    sameAs: SAME_AS,
    image: `${brandConfig.websiteBaseUrl}${seoDefaults.defaultOgImage}`,
  };
}

/** Reference to the Person node by @id. Any schema that names Ray as author,
 *  creator, or reviewer links here instead of restating the Person. The
 *  market-report Dataset creator should adopt this. */
export function personRef() {
  return { '@id': `${brandConfig.websiteBaseUrl}/#ray` } as const;
}

/** Reference to the Organization node (the Relta Real Estate brand) by @id. */
export function organizationRef() {
  return { '@id': `${brandConfig.websiteBaseUrl}/#company` } as const;
}

/**
 * The licensed person. The credential node is what lets a parser verify the
 * licence against the public BCFSA register rather than take the jobTitle on
 * faith.
 */
export function getPersonSchema() {
  const site = brandConfig.websiteBaseUrl;
  const licence = brandConfig.licence;
  return {
    '@type': 'Person',
    '@id': `${site}/#ray`,
    name: brandConfig.agentName,
    jobTitle: 'REALTOR®',
    description: `${brandConfig.agentName} is a licensed REALTOR® in Vancouver, British Columbia, with ${brandConfig.brokerageName}, and operates ${brandConfig.companyDisplayName} at relta.ca.`,
    image: `${site}${brandConfig.agentHeadshotPath}`,
    affiliation: organizationRef(),
    knowsAbout: KNOWS_ABOUT,
    hasOccupation: {
      '@type': 'Occupation',
      name: 'Real Estate Agent',
      occupationLocation: { '@type': 'State', name: licence.jurisdiction },
    },
    hasCredential: {
      '@type': 'EducationalOccupationalCredential',
      name: `${licence.jurisdiction} real estate licence (${licence.category.toLowerCase()})`,
      credentialCategory: 'license',
      identifier: licence.number,
      url: licence.registerUrl,
      recognizedBy: {
        '@type': 'GovernmentOrganization',
        name: licence.regulatorName,
        alternateName: licence.regulatorShortName,
        url: licence.regulatorUrl,
      },
    },
    memberOf: {
      '@type': 'Organization',
      name: brandConfig.boardMembership,
    },
    worksFor: {
      '@type': 'Organization',
      name: brandConfig.brokerageName,
      address: {
        '@type': 'PostalAddress',
        streetAddress: '500-666 Burrard Street',
        addressLocality: 'Vancouver',
        addressRegion: 'BC',
        postalCode: 'V6C 3P6',
        addressCountry: 'CA',
      },
    },
    url: `${site}/about-ray`,
    email: brandConfig.primaryContactEmail,
    telephone: brandConfig.phone,
    sameAs: SAME_AS,
  };
}

/**
 * Last substantive edit to the /about-ray copy or credentials. Bump it in the
 * same change that edits the page; it feeds ProfilePage.dateModified.
 */
export const ABOUT_RAY_DATE_MODIFIED = '2026-10-07';

/** ProfilePage for /about-ray with the full Person as mainEntity. */
export function getProfilePageSchema() {
  const site = brandConfig.websiteBaseUrl;
  return {
    '@context': 'https://schema.org',
    '@type': 'ProfilePage',
    '@id': `${site}/about-ray#profile`,
    url: `${site}/about-ray`,
    name: `About ${brandConfig.agentName}`,
    dateModified: ABOUT_RAY_DATE_MODIFIED,
    isPartOf: { '@id': `${site}/#website` },
    mainEntity: getPersonSchema(),
  };
}

/**
 * Site-wide entity graph, emitted once from the root layout:
 * Organization (the software company) + RealEstateAgent (the licensed agent)
 * + Person (Ray) + WebSite with SearchAction (the ?finder=ai&fq= deep link
 * opens the Home Finder prefilled) + WebApplication (the platform).
 */
export function getSiteGraphSchema() {
  const site = brandConfig.websiteBaseUrl;
  return {
    '@context': 'https://schema.org',
    '@graph': [
      getBrandOrganizationSchema(),
      getRealEstateAgentSchema(),
      getPersonSchema(),
      {
        '@type': 'WebSite',
        '@id': `${site}/#website`,
        name: brandConfig.companyDisplayName,
        alternateName: ALTERNATE_NAMES,
        description: ENTITY_DESCRIPTION,
        inLanguage: 'en-CA',
        url: site,
        publisher: { '@id': `${site}/#company` },
        potentialAction: {
          '@type': 'SearchAction',
          target: {
            '@type': 'EntryPoint',
            urlTemplate: `${site}/?finder=ai&fq={search_term_string}`,
          },
          'query-input': 'required name=search_term_string',
        },
      },
      // Declares the product category outright. Without this the graph reads as
      // an agent's brochure site, which is the category AI assistants otherwise
      // guess at (and guess wrong). The software is provided and created under the
      // brand (#company); the agent (#agent) is the licensed service provider.
      {
        '@type': 'WebApplication',
        '@id': `${site}/#platform`,
        name: brandConfig.companyDisplayName,
        alternateName: ALTERNATE_NAMES,
        description: ENTITY_DESCRIPTION,
        url: site,
        applicationCategory: 'RealEstateApplication',
        browserRequirements: 'Requires JavaScript.',
        operatingSystem: 'Any',
        inLanguage: 'en-CA',
        provider: { '@id': `${site}/#company` },
        creator: { '@id': `${site}/#company` },
        areaServed: AREA_SERVED_GRAPH,
        featureList: [
          'MLS® listing search across British Columbia',
          'Interactive map search',
          'Home finder',
          'Sold and expired listings for registered users',
          'Per-city and per-neighbourhood market statistics',
          'Mortgage, stress-test, and rent-versus-buy calculators',
          'Saved searches and listing alerts',
        ],
        offers: {
          '@type': 'Offer',
          price: '0',
          priceCurrency: 'CAD',
          description: 'Free to search and to create an account.',
        },
      },
    ],
  };
}

export { resolveArticleAttribution };

function articleAuthorNode(attribution: ArticleAttribution) {
  if (attribution.author.kind === 'person') {
    return {
      '@type': 'Person',
      ...personRef(),
      name: attribution.author.name,
      url: attribution.author.href,
    };
  }
  return {
    '@type': 'Organization',
    ...organizationRef(),
    name: attribution.author.name,
    url: brandConfig.websiteBaseUrl,
  };
}

export function getArticleSchema(post: {
  title: string;
  excerpt: string;
  publishedAt: string;
  updatedAt?: string;
  slug: string;
  author: { name: string };
  coverImage?: string;
  source?: string;
  reviewedAt?: string | null;
}) {
  const url = `${brandConfig.websiteBaseUrl}/blog/${post.slug}`;
  const attribution = resolveArticleAttribution(post);
  return {
    '@context': 'https://schema.org',
    '@type': post.source === 'rss-commentary' ? 'NewsArticle' : 'Article',
    headline: post.title,
    description: post.excerpt,
    datePublished: post.publishedAt,
    dateModified: post.updatedAt ?? post.publishedAt,
    mainEntityOfPage: { '@type': 'WebPage', '@id': url },
    author: articleAuthorNode(attribution),
    ...(attribution.reviewedBy
      ? {
          reviewedBy: {
            '@type': 'Person',
            ...personRef(),
            name: attribution.reviewedBy.name,
            url: attribution.reviewedBy.href,
          },
        }
      : {}),
    publisher: {
      '@type': 'Organization',
      '@id': `${brandConfig.websiteBaseUrl}/#company`,
      name: brandConfig.companyDisplayName,
      url: brandConfig.websiteBaseUrl,
      logo: {
        '@type': 'ImageObject',
        url: `${brandConfig.websiteBaseUrl}${seoDefaults.defaultOgImage}`,
      },
    },
    url,
    // Article requires an image for rich-result eligibility; fall back to the
    // absolute site OG image when a post has no cover.
    image: post.coverImage ?? `${brandConfig.websiteBaseUrl}${seoDefaults.defaultOgImage}`,
  };
}
